import { defineStore } from 'pinia'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import API from '@/api/apiUrls'
import { TRANSPORT_ACTIVITY_STAGES } from '@/constants/lifecyclePhases'
import type { StageKey } from '@/constants/lifecyclePhases'

//TODO: SPLIT UP IN MULTIPLE FILES LATER
export type { StageKey }

// export type ActivityMode = RE

export type ActivityDto = {
  id: number
  material: number
  equipment_factor?: number
  stage: StageKey | null
  mode: string
  value: number | null
  transportPath?: string[]
  transportUnit?: string
  impact?: number | null
  material_impact_calculation?: MaterialImpactCalculationDto
  material_phase_values?: MaterialPhaseValuesDto
}

export type StageImpactMap = { [K in StageKey]: number }

export type MaterialImpactCalculationDto = {
  base_total: number
  conversion_factor: number
  converted_total: number
  calculation_lifetime: number
  material_lifetime: number
  lifetime_factor: number
  total: number
}

export interface MaterialDtoBase {
  id: number
  block: number
  product: number
  name: string
  unit?: string
  lifetime: number
  conversion_factor: number
  impact_unit: string
  total?: number
  production?: number
  building?: number
  use_maintenance?: number
  demolition_processing?: number
  activities: ActivityDto[]
  impact_calculation?: MaterialImpactCalculationDto
}

export type MaterialPhaseValuesDto = Pick<
  MaterialDtoBase,
  'total' | 'production' | 'building' | 'use_maintenance' | 'demolition_processing'
> &
  Partial<StageImpactMap>

export type MaterialDto = MaterialDtoBase &
  Partial<StageImpactMap> & {
    baseImpacts?: StageImpactMap
  }

export interface ProductDtoBase {
  id: number
  name: string
  description: string
  lifetime: number
  impact_unit: string
  total?: number
  production?: number
  building?: number
  use_maintenance?: number
  demolition_processing?: number
  activities: ActivityDto[]
}
export type ProductDto = ProductDtoBase &
  Partial<StageImpactMap> & {
    baseImpacts?: StageImpactMap
  }

export type BuildingBlockType = 'material' | 'equipment'

export interface BuildingBlockDto {
  id: number
  calculation: number
  name: string
  unit: string
  hidden: boolean
  selected: boolean
  block_type?: BuildingBlockType
  total?: number
  materials: MaterialDto[]
}

export function isTransportStage(stage: StageKey) {
  return TRANSPORT_ACTIVITY_STAGES.includes(stage)
}

const duplicateURL = API.BUILDING_BLOCK.BUILDING_BLOCKS.DUPLICATE
const createFromProductURL = API.BUILDING_BLOCK.BUILDING_BLOCKS.CREATE_FROM_PRODUCT

export const useBuildingBlockStore = defineStore('buildingBlock', () => {
  const { api } = useApi()

  const baseStore = useBaseStore<BuildingBlockDto>({
    service: API.BUILDING_BLOCK.BUILDING_BLOCKS.INDEX,
    api
  })

  const duplicateBuildingBlock = async (item: BuildingBlockDto) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(duplicateURL(item.id)).post(item)
      if (Array.isArray(responseData)) {
        baseStore.addItems(responseData)
      } else {
        baseStore.addItems([responseData])
      }
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to duplicate calculation', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const createFromProduct = async (calculationId: number, productId: number, hidden = true) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api
        .service(createFromProductURL)
        .post({ calculation: calculationId, product_id: productId, hidden })
      if (Array.isArray(responseData)) {
        baseStore.addItems(responseData)
      } else {
        baseStore.addItems([responseData])
      }
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to create building block from product', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const setSelected = async (item: BuildingBlockDto, selected: boolean) => {
    return await baseStore.update({ ...item, selected })
  }

  return {
    ...baseStore,
    duplicateBuildingBlock,
    createFromProduct,
    setSelected
  }
})
